<template>
    <div class="content-wrapper">
        <div class="row align-items-center">
            <div class="col-md-12 col-lg-6">
                <div class="d-flex align-items-center">
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb p-0 d-flex align-items-center mb-primary">
                            <li class="breadcrumb-item page-header d-flex align-items-center">
                                <h4 class="mb-0">{{ $t('courses.course_details') }}</h4>
                            </li>
                            <template v-if="this.$can('view_courses')">
                                <li class="ml-2">|</li>
                                <li>
                                    <a :href="urlGenerator(courseWebUrl)"
                                       class="btn btn-link text-primary pl-2">{{
                                            $t('courses.back_to_all_course')
                                        }}</a>
                                </li>
                            </template>
                        </ol>
                    </nav>
                </div>
            </div>
            <div class="col-md-12 col-lg-6">
                <div class="d-flex justify-content-lg-end mb-primary">
                    <button type="button"
                            v-if="canViewStartLessonButton()"
                            class="btn btn-success"
                            @click="startLesson()">
                        {{ $t('lessons.start_lesson') }}
                    </button>
                </div>
            </div>
        </div>

        <div class="course-profile mb-primary">
            <div class="card card-with-shadow py-5 border-0" style="min-height: 220px;">
                <app-overlay-loader v-if="loader"/>

                <div v-else class="row">
                    <!-- Left Column - Course Details -->
                    <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-5 border-right">
                        <div class="media px-5 course-header-media align-items-center mb-4">
                            <div class="media-body course-info-header">
                                <p class="text-muted mb-0">{{ $t('Title') }}</p>
                                <p class="mb-0">{{ course.title }}</p>
                            </div>
                        </div>

                        <!-- Description Section -->
                        <div class="media mb-4 px-5">
                            <div class="media-body">
                                <p class="text-muted mb-0">{{ $t('description') }}</p>
                                <p class="mb-0" v-if="course.description">{{ course.description }}</p>
                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                            </div>
                        </div>

                        <!-- Passing Score Section -->
                        <div class="media mb-4 px-5 mb-xl-5">
                            <div class="media-body">
                                <p class="text-muted mb-0">{{ $t('courses.passing_score') }}</p>
                                <p class="mb-0" v-if="course.passing_score">{{ course.passing_score }}</p>
                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                            </div>
                        </div>
                        <div class="media mb-4 px-5 mb-xl-5">
                            <div class="media-body">
                                <p class="text-muted mb-0">{{ $t('departments') }}</p>
                                <div v-if="course.departments && course.departments.length">
                                    <p class="mb-0" v-for="(department, index) in course.departments" :key="index">
                                        {{ department.name }}
                                    </p>
                                </div>
                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Right Column - Dates & Additional Info -->
                    <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-7">
                        <div class="course-details px-5 mt-4 mt-sm-5 mt-md-0 mt-lg-0 mt-xl-0">
                            <div class="row">
                                <!-- Created and Updated Date Sections -->
                                <div class="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6 border-right">
                                    <div class="custom">
                                        <!-- Created By -->
                                        <div class="media mb-4 ">
                                            <div class="align-self-center mr-3">
                                                <app-icon name="user"/>
                                            </div>
                                            <div class="media-body">
                                                <p class="text-muted mb-0">{{ $t('created_by') }}</p>
                                                <p class="mb-0" v-if="course.creator?.full_name">
                                                    {{ course.creator.full_name }}</p>
                                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                                            </div>
                                        </div>
                                        <!-- Created Date -->
                                        <div class="media mb-4 ">
                                            <div class="align-self-center mr-3">
                                                <app-icon name="calendar"/>
                                            </div>
                                            <div class="media-body">
                                                <p class="text-muted mb-0">{{ $t('created_at') }}</p>
                                                <p class="mb-0" v-if="course.created_at">
                                                    {{ formatDateTimeToLocalDate(course.created_at) }}</p>
                                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                                            </div>
                                        </div>

                                        <!-- Updated Date -->
                                        <div class="media mb-4">
                                            <div class="align-self-center mr-3">
                                                <app-icon name="calendar"/>
                                            </div>
                                            <div class="media-body">
                                                <p class="text-muted mb-0">{{ $t('updated_at') }}</p>
                                                <p class="mb-0" v-if="course.updated_at">
                                                    {{ formatDateTimeToLocalDate(course.updated_at) }}</p>
                                                <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6"
                                     v-if="canDownloadCertificate()">
                                    <div class="media-body">
                                        <app-icon name="calendar"/>
                                        <p class="mb-0">{{ $t('Course Certificate') }}</p>
                                    </div>
                                    <button type="button"
                                            class="btn btn-success"
                                            @click="courseCertificate()">
                                        {{ $t('courses.download_certificate') }}
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- User Course Progress Section -->
        <div class="row px-5" v-if="!this.$can('create_lesson')">
            <li class="col-12 mb-4 breadcrumb-item page-header d-flex align-items-center">
                <h4 class="mb-0">{{ $t('userCourseProgresses.user_course_progress') }}</h4>
            </li>
            <template v-if="course.user_course_progress && course.user_course_progress.length">
                <div
                    v-for="(progress, index) in course.user_course_progress"
                    :key="progress.id"
                    class="col-12 col-sm-6 col-md-4 mb-3"
                >
                    <div class="card h-100 border-0 shadow-sm">
                        <div class="card-body">
                            <h6 class="text-muted mb-1">{{ $t('Attempt') }} {{ index + 1 }}</h6>
                            <p class="mb-1">
                                <strong>{{ $t('score') }}:</strong> {{ progress.score }}%
                            </p>
                            <p class="mb-1">
                                <strong>{{ $t('status') }}:</strong>
                                <span :class="'text-' + progress.status.class">
                            {{ progress.status.translated_name }}
                        </span>
                            </p>
                            <p class="mb-0 text-muted" style="font-size: 0.8rem;">
                                {{ formatDateTimeToLocalDate(progress.updated_at) }}
                            </p>
                            <br><br>
                            <!-- Score Progress Bar -->
                            <p><b>Progress Bar</b></p>
                            <div class="progress">
                                <div
                                    class="progress-bar"
                                    role="progressbar"
                                    :style="{ width: progress.progress_percent + '%' }"
                                    :aria-valuenow="progress.progress_percent"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                >
                                    {{ progress.progress_percent }}%
                                </div>
                            </div>
                            <div class="mt-3">
                                <button class="btn btn-primary btn-sm" @click="viewSummary(progress)">
                                    {{ $t('View Summary') }}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

            </template>

            <div v-else class="col-12">
                <p class="text-muted">{{ $t('userCourseProgresses.no_progress_available') }}</p>
            </div>
        </div>

        <user-course-summary
            v-if="showSummaryModal && summaryData"
            :value="summaryData"
            @close="closeSummaryModal"
            :hide-footer="true"
        />
        <div class="content-wrapper" v-if="this.$can('create_lesson')">
            <app-page-top-section :title="$t('course_settings')"/>
            <app-tab
                :tabs="tabs"
                icon="settings"
            />
        </div>
    </div>
</template>

<script>
import {axiosGet, urlGenerator} from '../../../../../common/Helper/AxiosHelper';
import {FormMixin} from "../../../../../core/mixins/form/FormMixin";
import CourseCreateEditModal from "../../Learning/Course/CourseCreateEditModal";
import {formatDateTimeToLocalDate} from "../../../../../common/Helper/Support/DateTimeHelper";
import {mapState} from "vuex";
import {
    COURSE_LESSON_WEB,
    COURSE_PDF,
    COURSE_PROGRESS_SUMMARY,
    COURSEWEB
} from "../../../../Config/ApiUrl";
import EmployeeContextMenu from "../../Employee/Components/EmployeeContextMenu.vue";
import UserCourseSummary from "../UserCourseProgress/UserCourseSummary.vue";


export default {
    name: "CourseDetails",
    mixins: [FormMixin],
    components: {UserCourseSummary, EmployeeContextMenu, CourseCreateEditModal},
    props: {
        courseId: {},
        employeeId: {
            type: String,
        },
    },
    data() {
        return {
            course: {},
            courseWebUrl: COURSEWEB,
            urlGenerator,
            formatDateTimeToLocalDate,
            promptAction: '',
            modalClass: '',
            promptIcon: '',
            promptSubtitle: '',
            showSummaryModal: false,
            summaryData: null,
            tabs: [
                {
                    name: this.$t('lessons'),
                    component: "app-lessons",
                    permission: this.$can('view_lessons'),
                    props: {
                        course_id: this.courseId
                    }
                },
            ]
        };
    },
    mounted() {
        // Fetch course details on component mount
        this.$store.dispatch("getCourseDetails", this.courseId);
        this.$store.dispatch('getUserInformation');
    },
    methods: {
        canDownloadCertificate() {
            if (!this.course) return false;
            const userId = this.$store.getters.getUserInformation?.id;
            const userHasProgress = Array.isArray(this.course.user_course_progress) &&
                this.course.user_course_progress.some(
                    progress => progress.user_id === userId && progress.status?.name === 'status_passed'
                );
            return userHasProgress;
        },
        async courseCertificate() {
            try {
                const url = COURSE_PDF.replace('{course}', this.courseId);

                axiosGet(`${url}`)
                    .then(({data}) => {
                        if (data?.status && data?.download_url) {
                            // Convert to full URL if it's a relative path
                            const downloadUrl = data.download_url.startsWith('http')
                                ? data.download_url
                                : `${window.location.origin}${data.download_url}`;

                            // Open in a new browser tab
                            window.open(downloadUrl, '_blank');
                        } else {
                            this.$toastr.e("Certificate not available.");
                        }
                    })
                    .catch(() => this.$toastr.e("Failed to load certificate"))
                    .finally(() => (this.loading = false));
            } catch (error) {
                console.error('Error downloading certificate:', error);
            }
        },
        startLesson() {
            window.location.href = urlGenerator(`${COURSE_LESSON_WEB}/${this.courseId}`); // Redirect to the generated URL
        },
        canViewStartLessonButton() {
            if (!this.course) return false;
            const userId = this.$store.getters.getUserInformation?.id;
            const hasLessons = Array.isArray(this.course.lessons) && this.course.lessons.length > 0;
            const hasQuestions = this.course.lessons?.some(
                lesson => Array.isArray(lesson.questions) && lesson.questions.length > 0
            );
            const userHasProgress = Array.isArray(this.course.user_course_progress) &&
                this.course.user_course_progress.some(
                    progress => progress.user_id === userId && progress.status.name === 'status_passed'
                );
            if (userHasProgress) {
                const attemptCount = this.course.user_course_progress.length;
                return !userHasProgress && attemptCount <= 3 && hasLessons && hasQuestions;
            }

            return !userHasProgress && hasLessons && hasQuestions;
        },

        async viewSummary(progress) {
            this.summaryData = null; // reset
            try {
                const progressUrl = COURSE_PROGRESS_SUMMARY.replace('{progress}', progress.id);

                axiosGet(`${progressUrl}`).then(({data}) => {
                    this.summaryData = data.summary;
                    this.showSummaryModal = true;
                })
                    .catch(error => {
                        console.error('Failed to load summary:', error);
                        this.$toasted.error(this.$t('Unable to load summary.'));
                    })
                    .finally(() => {
                        // If you have loading indicator, update it here
                    });

            } catch (error) {
                console.error('Unexpected error in viewSummary():', error);
                this.$toasted.error(this.$t('Something went wrong.'));
            }
        },

        closeSummaryModal() {
            this.showSummaryModal = false;
            this.summaryData = null;
        }
    },
    computed: {
        ...mapState({
            courseDetails: state => state.course.course,
            loader: state => state.loading,
        }),
        progress() {
            return this.course.progress_percentage;
        }
    },
    watch: {
        courseDetails: {
            handler: function (course) {
                this.course = course;

                // Check if course.title exists
                if (course.title) {
                    // Ensure document.title is a string and exists
                    if (typeof document.title === 'string' && document.title) {
                        let title = document.title.split('-');
                        title[0] = course.title;
                        document.titile = title.join(' - ');
                    } else {
                        // If document.title is undefined or not a string, initialize it properly
                        document.title = course.title;
                    }
                }
            },
            deep: true
        }
    }
};
</script>
